import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import {
  BaseBox,
  Body1,
  Heading3,
  Heading4,
  DoconDropdown,
  DoconDatePicker,
  FlexBox,
  Icons,
  CenterBox,
  PrimaryButton,
  SecondaryButton,
} from '@docon/docon-ui-library';
import {
  getClinics,
  getClinicDetails,
} from 'redux/reducer/doctor';
import {
  getNextAvailableSlot, getOnlineSchedule,
} from 'redux/reducer/shift';
import SlotsContainer, { Heading as SlotHeading } from './Slots';
import { Loader } from 'shared';
import { ClinicIcon } from 'shared/icons';
import { bgColor, colors } from 'utilities/colorConfig';

const Header = styled(FlexBox)`
  gap: 0 12px;
  align-items: center;

  .clinicDoconDropdown {
    width: 100%;
    .reactSelect__control {
      height: 0;
      min-height: 0;
      overflow: hidden;
      border: 0;
    }
  }

  ${Heading3} {
    font-weight: 500;
    color: inherit;
  }

  ${Heading4} {
    color: inherit;
    opacity: 0.8;
  }
`;

const Row = styled(FlexBox)`
  position: sticky;
  top: 0;
  align-items: center;
  justify-content: space-between;
  padding: 5px var(--paddingLeftRight);
  background-color: #eaedf1;
`;

const NoSlotContainer = styled(FlexBox)`
  max-width: 70%;
  gap: 12px 0;
  position: relative;
  top: 35%;
  margin: 0 auto;
  text-align: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 var(--paddingLeftRight);
  transform: translateY(-50%);

  button {
    margin: 0;
    width: 100%;
    font-size: 16px;
    font-weight: 600;
  }
`;

const Root = styled(BaseBox)`
  height: 100%;
  display: flex;
  flex-direction: column;

  ${Icons.ChevronIcon} {
    cursor: pointer;
  }

  ${SlotsContainer} {
    flex: 1;
    flex-wrap: nowrap;
    padding: 5px var(--paddingLeftRight);

    ${SlotHeading} {
      position: sticky;
      top: -5px;
      background-color: ${bgColor.base};
    }
  }

  ${() => isMobileOnly && css`
    ${NoSlotContainer} {
      max-width: 100%;
    }
  `}
`;

interface IProps {
  isReschedule?: boolean;
  doctor: any;
  appointment?: any;
  activeClinicID: any;
  selectedDate: any;
  onChange: (x: any) => void;
  parentCallback: (x: any) => void;
}

function getLabel(clinic: any) {
  const { name, address: { area = '', city = '' } = {} } = clinic || {};
  return (
    <FlexBox gap={'12px'}>
      <CenterBox size={40} bg={bgColor.warning} borderRadius={'50%'}>
        <ClinicIcon color={colors.white} width={20} />
      </CenterBox>
      <BaseBox flex={1}>
        <Heading4 title={name} fontWeight={500} />
        <Body1 title={`${area}, ${city}`} />
      </BaseBox>
    </FlexBox>
  );
}

function TimeSlots({
  isReschedule,
  activeClinicID,
  selectedDate,
  doctor,
  appointment,
  onChange,
  parentCallback,
}: IProps) {

  const [openClinicDropdown, setOpenClinicDropdown] = useState(false);

  const {
    clinicsByDocId,
    clinicByClinicId,
  } = useSelector(state => state.doctor);

  const {
    onlineScheduleByClinicId,
    nextAvailableSlotByClinicId,
    isFetchingNextAvailableSlot,
    isFetchingOnlineSchedule,
  } = useSelector(state => state.shift);

  const dispatch = useDispatch();

  const dateChangeTrigger = useRef('');
  const activeClinic = clinicByClinicId[activeClinicID] || {};
  const clinics = clinicsByDocId[doctor.doctorID] || [];
  const nextAvailableSlot = nextAvailableSlotByClinicId[activeClinicID] || {};

  useEffect(() => {
    dispatch(getClinicDetails(activeClinicID));
  }, [activeClinicID]);

  useEffect(() => {
    if (!isEmpty(activeClinic))
      parentCallback(renderHeader());
  }, [activeClinic, openClinicDropdown]);

  useEffect(() => {
    dispatch(getNextAvailableSlot({
      date: moment(selectedDate).format('YYYY-MM-DD'),
      doctorID: doctor.doctorID,
      clinicID: activeClinicID,
    }));
    dispatch(getOnlineSchedule({
      date: moment(selectedDate).format('YYYY-MM-DD'),
      doctorID: doctor.doctorID,
      clinicID: activeClinicID,
    }));
  }, [activeClinicID, selectedDate]);

  useEffect(() => {
    if (isEmpty(clinics)) {
      dispatch(getClinics(doctor.doctorID));
    }
  }, []);

  function handleChange({ name: key, value, dateString }: any) {
    if (key === 'activeClinicID')
      setOpenClinicDropdown(false);

    switch (key) {
      case 'selectedDate':
        dateChangeTrigger.current = dateString ? 'calendar' : 'arrowIcon';
        break;
      default:
        dateChangeTrigger.current = '';
    }

    onChange({ key, value });
  }

  function handleClick(event) {
    const id = event?.currentTarget?.id;
    switch (id) {
      case 'upDownArrow':
        setOpenClinicDropdown(!openClinicDropdown);
        break;
      case 'leftArrow':
        return handleChange({ name: 'selectedDate', value: moment(selectedDate).add(-1, 'day') });
      case 'rightArrow':
        return handleChange({ name: 'selectedDate', value: moment(selectedDate).add(1, 'day') });
      case 'nextSlot':
        return handleChange({ name: 'selectedDate', value: moment(nextAvailableSlot.date) });
      case 'callClinic':
        return window.location.href = `tel: ${activeClinic.number}`;
      default:
        break;
    }
  }

  function handleSlotClick({ slot }: any) {
    onChange({ key: 'activeSlot', value: slot });
  }

  function renderHeader() {
    const { name, address: { area = '', city = '' } = {} } = activeClinic;
    return (
      <Header>
        <BaseBox>
          <Heading3 title={name} />
          <Heading4 title={`${area}, ${city}`} />
        </BaseBox>

        {
          clinics?.length > 1 ? (
            <Icons.ChevronIcon
              id='upDownArrow'
              type={openClinicDropdown ? 'up' : 'down'}
              width={20}
              color={colors.white}
              onClick={handleClick}
            />
          ) : null
        }

        {
          isEmpty(clinics) ? null : (
            <DoconDropdown
              menuIsOpen={openClinicDropdown}
              name={'activeClinicID'}
              label=''
              className='clinicDoconDropdown'
              value={activeClinicID}
              options={clinics.map(clinic => ({
                label: getLabel(clinic),
                value: clinic.clinicID,
              }))}
              onChange={handleChange}
            />
          )
        }
      </Header>
    );
  }

  const isLoading = isFetchingNextAvailableSlot || isFetchingOnlineSchedule;
  const today = moment();
  const isSlotAvailable = nextAvailableSlot.date !== 0 && moment(nextAvailableSlot.date, 'YYYY-MM-DD') <= selectedDate;

  return (
    <Root>
      <Row>
        <Icons.ChevronIcon
          id='leftArrow'
          type='left'
          width={20}
          visibility={moment(selectedDate) <= today ? 'hidden' : ''}
          onClick={handleClick}
        />
        <DoconDatePicker
          inputReadOnly
          name={'selectedDate'}
          value={selectedDate}
          format={'ddd, DD/MM/YYYY'}
          placeholder='DD/MM/YYYY'
          minDate={moment()}
          enableValueSanitize={true}
          onChange={handleChange}
          onBlur={handleChange}
        />
        <Icons.ChevronIcon
          id='rightArrow'
          type='right'
          width={20}
          onClick={handleClick}
        />
      </Row>

      {
        isLoading ? <Loader height={'calc(100% - 55px)'} /> : (
          isSlotAvailable ? (
            <SlotsContainer
              isReschedule={isReschedule}
              dateChangeTrigger={dateChangeTrigger.current}
              appointment={appointment}
              onlineSchedule={onlineScheduleByClinicId[activeClinicID]}
              selectedDate={selectedDate}
              clinic={activeClinic}
              doctor={doctor}
              onSlotClick={handleSlotClick}
            />
          ) : (
            <NoSlotContainer>
              <Heading3 title={`No slots available ${nextAvailableSlot.date !== 0 ? 'for this day' : ''}`} />
              <PrimaryButton
                id='nextSlot'
                hidden={nextAvailableSlot.date == 0}
                title={`Next available on ${moment(nextAvailableSlot.date).format('ddd, DD/MM/YYYY')}`}
                onClick={handleClick}
              />
              <SecondaryButton
                id='callClinic'
                title={'Call Clinic'}
                onClick={handleClick}
              />
            </NoSlotContainer>
          )
        )
      }
    </Root>
  );
}

export default TimeSlots;